import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";

export const getCashDocumentUrl = (value = "") => {
  const file = String(value).trim();
  if (!file) return "";

  // Preserve existing full URLs.
  if (/^https?:\/\//i.test(file)) return file;

  const isLocal = ["localhost", "127.0.0.1"].includes(
    window.location.hostname
  );

  const baseUrl = isLocal
    ? "http://localhost:3001"
    : "https://indigoinfra.in";

  const filename = file
    .split(/[?#]/)[0]
    .replace(/\\/g, "/")
    .split("/")
    .pop();

  if (!filename) return "";

  return `${baseUrl}/img/cash-entries/${encodeURIComponent(filename)}`;
};

export const getCashDocuments = (entry) => {
  const attachments = Array.isArray(entry?.image)
    ? entry.image
    : [];

  return attachments
    .map((attachment, index) => {
      const item =
        typeof attachment === "string"
          ? { url: attachment }
          : attachment;

      if (!item?.url) return null;

      const url = getCashDocumentUrl(item.url);
      if (!url) return null;

      const path = String(item.url).split(/[?#]/)[0];
      const extension = path.split(".").pop().toLowerCase();
      const mimeType = String(
        item.mimeType || item.mimetype || ""
      ).toLowerCase();

      const type =
        mimeType === "application/pdf" || extension === "pdf"
          ? "pdf"
          : mimeType.startsWith("image/") ||
              ["jpg", "jpeg", "png", "gif", "webp"].includes(extension)
            ? "image"
            : "other";

      return {
        url,
        name:
          item.originalName ||
          item.name ||
          path.split("/").pop() ||
          `Document ${index + 1}`,
        type
      };
    })
    .filter(Boolean);
};

const CashDocumentPreviewModal = ({
  show,
  handleClose,
  entry,
  initialIndex = 0
}) => {
  const documents = getCashDocuments(entry);

  const [index, setIndex] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const selectedIndex = Number.isInteger(initialIndex)
      ? initialIndex
      : 0;

    setIndex(
      Math.min(
        Math.max(0, selectedIndex),
        Math.max(0, documents.length - 1)
      )
    );
  }, [entry, show, initialIndex, documents.length]);

  const activeDocument = documents[index];

  useEffect(() => {
    setRotation(0);
    setZoom(1);
    setLoading(true);
    setError(false);
  }, [activeDocument?.url, show]);

  const iconStyle = {
    border: "1px solid #dee2e6",
    borderRadius: "4px",
    padding: "5px 8px",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    cursor: "pointer",
    fontSize: "14px"
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "20px" }}>
          Cash Document Preview — {entry?.requestNumber || "—"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {activeDocument ? (
          <>
            <div className="d-flex align-items-center gap-2 mb-3">
              {documents.map((file, fileIndex) => (
                <button
                  key={`${file.url}-${fileIndex}`}
                  type="button"
                  title={`View attachment ${fileIndex + 1}`}
                  aria-label={`View attachment ${fileIndex + 1}`}
                  aria-pressed={index === fileIndex}
                  onClick={() => setIndex(fileIndex)}
                  style={{
                    ...iconStyle,
                    background:
                      index === fileIndex ? "#1565c0" : "#ffffff",
                    color:
                      index === fileIndex ? "#ffffff" : "#1565c0"
                  }}
                >
                  <i
                    className={
                      file.type === "pdf"
                        ? "fa fa-file-pdf-o"
                        : "fa fa-paperclip"
                    }
                    aria-hidden="true"
                  />
                  <span>{fileIndex + 1}</span>
                </button>
              ))}
            </div>

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div className="d-flex flex-wrap gap-2">
                {activeDocument.type === "image" && (
                  <>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => setRotation((value) => value - 90)}
                    >
                      ⟲ Rotate
                    </Button>

                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => setRotation((value) => value + 90)}
                    >
                      ⟳ Rotate
                    </Button>

                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() =>
                        setZoom((value) => Math.max(0.5, value - 0.2))
                      }
                    >
                      − Zoom
                    </Button>

                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() =>
                        setZoom((value) => Math.min(3, value + 0.2))
                      }
                    >
                      + Zoom
                    </Button>

                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setZoom(1);
                        setRotation(0);
                      }}
                    >
                      Reset
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div
              className="small text-muted mb-2"
              style={{ overflowWrap: "anywhere" }}
            >
              {activeDocument.name}
            </div>

            <div
              style={{
                height: "65vh",
                overflow: "auto",
                background: "#f5f7fa",
                border: "1px solid #dee2e6",
                borderRadius: "6px",
                padding: "12px"
              }}
            >
              {activeDocument.type === "image" && (
                <>
                  {loading && !error && (
                    <div className="text-center p-3" role="status">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        aria-hidden="true"
                      />
                      Loading document...
                    </div>
                  )}

                  {error ? (
                    <div className="text-center p-4 text-danger">
                      Unable to load image. Use Open / Download.
                    </div>
                  ) : (
                    <div className="text-center">
                      <img
                        key={activeDocument.url}
                        src={activeDocument.url}
                        alt={activeDocument.name}
                        onLoad={() => setLoading(false)}
                        onError={() => {
                          setLoading(false);
                          setError(true);
                        }}
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          transform: `scale(${zoom}) rotate(${rotation}deg)`,
                          transformOrigin: "center",
                          margin: "24px auto"
                        }}
                      />
                    </div>
                  )}
                </>
              )}

              {activeDocument.type === "pdf" && (
                <iframe
                  key={activeDocument.url}
                  src={activeDocument.url}
                  title={`Cash document ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: 0
                  }}
                />
              )}

              {activeDocument.type === "other" && (
                <div className="text-center p-4">
                  Preview unavailable for this file.
                  Please use Open / Download.
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center text-muted p-4">
            No cash documents attached.
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleClose}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CashDocumentPreviewModal;